/**
 * Branding Context Provider
 * 
 * Manages white-label branding configuration for the entire application.
 * Loads branding from BFF API, caches it, applies theme colors and favicon,
 * and exposes configuration to all components and plugins.
 * 
 * Features:
 * - Automatic branding fetch on app start
 * - 5-minute localStorage cache for performance
 * - Graceful fallback to defaults on error
 * - CSS variable injection for custom colors
 * - Favicon and document title updates
 * - Global exposure for plugins via globalThis
 * 
 * @module contexts/BrandingContext
 * @version 1.0.0
 */

import React, { createContext, useContext, useEffect, useState, useCallback, type JSX } from 'react';
import type {
  BrandingConfig,
  BrandingContextValue,
  BrandingColors,
} from '../types/branding';
import {
  DEFAULT_BRANDING_CONFIG,
  BRANDING_CACHE_CONFIG,
  CSS_VAR_MAPPING,
} from '../types/branding';

// Create context with null as initial value
const BrandingContext = createContext<BrandingContextValue | null>(null);

// Global branding key for plugin access
declare global {
  interface Window {
    __EXPERIENCE_BRANDING__?: BrandingConfig;
  }
}

/**
 * Branding Provider Component
 * 
 * Wraps the application to provide branding configuration to all children.
 * Must be placed high in the component tree, typically in main.tsx.
 * 
 * @example
 * ```tsx
 * <BrandingProvider>
 *   <App />
 * </BrandingProvider>
 * ```
 */
export function BrandingProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [config, setConfig] = useState<BrandingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Load branding configuration from API
   * Implements caching, error handling, and graceful fallback
   */
  const loadBranding = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Check cache first for instant load
      const cached = getCachedBranding();
      if (cached) {
        console.debug('[Branding] Using cached configuration');
        setConfig(cached);
        applyBranding(cached).catch(err => console.error('[Branding] Failed to apply cached branding:', err));
        // Continue to fetch in background for freshness
      }

      // Fetch from API
      console.debug('[Branding] Fetching from API');
      const response = await fetch('/api/configs/branding', {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Branding API returned ${response.status}: ${response.statusText}`);
      }

      const data: BrandingConfig = await response.json();
      
      // Validate required fields
      if (!data.appName || !data.logos?.primary) {
        throw new Error('Invalid branding configuration: missing required fields');
      }
      
      // Merge with defaults to ensure all fields exist
      const merged: BrandingConfig = {
        ...DEFAULT_BRANDING_CONFIG,
        ...data,
        logos: {
          ...DEFAULT_BRANDING_CONFIG.logos,
          ...data.logos,
        },
        theme: data.theme ? {
          ...DEFAULT_BRANDING_CONFIG.theme,
          ...data.theme,
        } : DEFAULT_BRANDING_CONFIG.theme,
        footer: data.footer ? {
          ...DEFAULT_BRANDING_CONFIG.footer,
          ...data.footer,
        } : DEFAULT_BRANDING_CONFIG.footer,
        header: data.header ? {
          ...DEFAULT_BRANDING_CONFIG.header,
          ...data.header,
        } : DEFAULT_BRANDING_CONFIG.header,
      };
      
      console.info('[Branding] Loaded configuration:', {
        appName: merged.appName,
        theme: merged.theme?.mode,
        hasCustomColors: !!merged.theme?.colors,
      });
      
      setConfig(merged);
      setCachedBranding(merged);
      await applyBranding(merged);

    } catch (err) {
      const error = err as Error;
      console.error('[Branding] Failed to load configuration:', error.message);
      setError(error);
      
      // Fall back to defaults (non-fatal error)
      console.warn('[Branding] Using default configuration as fallback');
      setConfig(DEFAULT_BRANDING_CONFIG);
      applyBranding(DEFAULT_BRANDING_CONFIG).catch(err => console.error('[Branding] Failed to apply default branding:', err));
      
    } finally {
      setLoading(false);
    }
  }, []);

  // Load branding on mount
  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  const value: BrandingContextValue = {
    config,
    loading,
    error,
    refresh: loadBranding,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

/**
 * Hook to access branding configuration
 * 
 * Must be used within a BrandingProvider.
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { config, loading, error } = useBrandingContext();
 *   
 *   if (loading) return <div>Loading branding...</div>;
 *   if (error) return <div>Using default branding</div>;
 *   
 *   return <div>{config?.appName}</div>;
 * }
 * ```
 * 
 * @throws Error if used outside BrandingProvider
 */
export function useBrandingContext(): BrandingContextValue {
  const context = useContext(BrandingContext);
  
  if (!context) {
    throw new Error(
      'useBrandingContext must be used within BrandingProvider. ' +
      'Ensure BrandingProvider wraps your component tree in main.tsx'
    );
  }
  
  return context;
}

// ============================================================================
// Cache Management
// ============================================================================

interface CachedBranding {
  config: BrandingConfig;
  timestamp: number;
}

/**
 * Get branding from localStorage cache
 * Returns null if cache is expired or invalid
 */
function getCachedBranding(): BrandingConfig | null {
  try {
    const cached = localStorage.getItem(BRANDING_CACHE_CONFIG.CACHE_KEY);
    if (!cached) {
      return null;
    }

    const parsed: CachedBranding = JSON.parse(cached);
    const age = Date.now() - parsed.timestamp;

    // Check if cache expired
    if (age > BRANDING_CACHE_CONFIG.CACHE_TTL) {
      console.debug('[Branding] Cache expired, removing');
      localStorage.removeItem(BRANDING_CACHE_CONFIG.CACHE_KEY);
      return null;
    }

    // Validate cached config has required fields
    if (!parsed.config?.appName || !parsed.config?.logos?.primary) {
      console.warn('[Branding] Invalid cached config, removing');
      localStorage.removeItem(BRANDING_CACHE_CONFIG.CACHE_KEY);
      return null;
    }

    console.debug(`[Branding] Cache hit (age: ${Math.round(age / 1000)}s)`);
    return parsed.config;
    
  } catch (err) {
    console.warn('[Branding] Failed to read cache:', err);
    // Clear invalid cache
    try {
      localStorage.removeItem(BRANDING_CACHE_CONFIG.CACHE_KEY);
    } catch {
      // Ignore if localStorage unavailable
    }
    return null;
  }
}

/**
 * Store branding in localStorage cache
 */
function setCachedBranding(config: BrandingConfig): void {
  try {
    const cached: CachedBranding = {
      config,
      timestamp: Date.now(),
    };
    localStorage.setItem(
      BRANDING_CACHE_CONFIG.CACHE_KEY,
      JSON.stringify(cached)
    );
    console.debug('[Branding] Configuration cached');
  } catch (err) {
    // Non-fatal: cache write failure doesn't break app
    console.warn('[Branding] Failed to cache configuration:', err);
  }
}

// ============================================================================
// Branding Application
// ============================================================================

/**
 * Apply branding configuration to the DOM
 * Updates colors, favicon, title, metadata, and exposes to plugins
 */
async function applyBranding(config: BrandingConfig): Promise<void> {
  try {
    // Apply theme colors (from custom OR from themes.yaml!) via ThemeManager
    // 
    // MAGNUM: Respect allowUserOverride setting
    // - When allowUserOverride is FALSE: Admin theme is ENFORCED, user preference ignored
    // - When allowUserOverride is TRUE: Skip if user has explicitly chosen a theme
    const userTheme = localStorage.getItem('experience-theme');
    const allowUserOverride = config.theme?.allowUserOverride !== false; // Default to true
    
    if (config.theme?.colors) {
      if (!allowUserOverride) {
        // Admin enforcement mode: ALWAYS apply branding colors, clear user preference
        if (userTheme) {
          console.debug(`[Branding] Admin theme enforced (allowUserOverride: false), clearing user preference: ${userTheme}`);
          localStorage.removeItem('experience-theme');
        }
        await applyCustomColors(config.theme.colors);
        console.debug(`[Branding] Applied ${Object.keys(config.theme.colors).length} colors from enforced theme: ${config.theme.mode}`);
      } else if (!userTheme) {
        // User override allowed but no user preference - apply branding
        await applyCustomColors(config.theme.colors);
        console.debug(`[Branding] Applied ${Object.keys(config.theme.colors).length} colors from theme: ${config.theme.mode}`);
      } else {
        // User override allowed AND user has preference - skip branding colors
        console.debug(`[Branding] Skipping branding colors - user has theme preference: ${userTheme}`);
      }
    }

    // Load external CSS if specified
    if (config.theme?.brandCssUrl) {
      loadBrandCss(config.theme.brandCssUrl);
    }

    // Update favicon
    if (config.favicon) {
      updateFavicon(config.favicon);
    }

    // Update document title
    if (config.appName) {
      updateDocumentTitle(config.appName);
    }

    // Update meta description
    if (config.metadata?.description) {
      updateMetaDescription(config.metadata.description);
    }

    // Update meta keywords
    if (config.metadata?.keywords) {
      updateMetaKeywords(config.metadata.keywords);
    }

    // Expose to plugins via globalThis
    (globalThis as any).__EXPERIENCE_BRANDING__ = config;
    
    console.debug('[Branding] Configuration applied to DOM');
    
  } catch (err) {
    console.error('[Branding] Failed to apply configuration:', err);
  }
}

/**
 * Apply custom color overrides to CSS variables via ThemeManager
 */
async function applyCustomColors(colors: any): Promise<void> {
  const { themeManager } = await import('../core/theme/ThemeManager');
  
  // Normalize colors to CSS variable format
  const normalizedColors: Record<string, string> = {};
  Object.entries(colors).forEach(([key, value]) => {
    // Try mapped variable first, otherwise use key directly as CSS variable
    const cssVar = CSS_VAR_MAPPING[key] || key;
    const normalizedKey = cssVar.startsWith('--') ? cssVar.slice(2) : cssVar;
    normalizedColors[normalizedKey] = String(value);
  });
  
  themeManager.setColors('branding', normalizedColors);
  console.debug(`[Branding] Applied ${Object.keys(normalizedColors).length} custom color(s) via ThemeManager`);
}

/**
 * Load external brand CSS file
 */
function loadBrandCss(url: string): void {
  const linkId = 'tenant-brand-css';
  let link = document.getElementById(linkId) as HTMLLinkElement | null;
  
  if (!link) {
    link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.type = 'text/css';
    document.head.appendChild(link);
  }
  
  // Only reload if URL changed
  if (link.href !== url) {
    link.href = url;
    console.debug('[Branding] Loading external CSS:', url);
  }
}

/**
 * Update favicon
 */
function updateFavicon(url: string): void {
  let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
  
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/png';
    document.head.appendChild(link);
  }
  
  if (link.href !== url) {
    link.href = url;
    console.debug('[Branding] Updated favicon:', url);
  }
}

/**
 * Update document title
 */
function updateDocumentTitle(appName: string): void {
  if (document.title !== appName) {
    document.title = appName;
    console.debug('[Branding] Updated document title:', appName);
  }
}

/**
 * Update meta description tag
 */
function updateMetaDescription(description: string): void {
  let meta = document.querySelector("meta[name='description']") as HTMLMetaElement;
  
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'description';
    document.head.appendChild(meta);
  }
  
  if (meta.content !== description) {
    meta.content = description;
    console.debug('[Branding] Updated meta description');
  }
}

/**
 * Update meta keywords tag
 */
function updateMetaKeywords(keywords: string): void {
  let meta = document.querySelector("meta[name='keywords']") as HTMLMetaElement;
  
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'keywords';
    document.head.appendChild(meta);
  }
  
  if (meta.content !== keywords) {
    meta.content = keywords;
    console.debug('[Branding] Updated meta keywords');
  }
}

/**
 * Re-export hook for convenience
 */
export { useBrandingContext as useBranding };

